<template>
  <div class="wrap">
    <header class="top">
      <div>
        <b>{{ user?.username }}</b> <small>({{ user?.role }})</small>
      </div>

      <div class="controls">
        <span class="state">State: <b>{{ state }}</b></span>
        <button @click="setState('READY')" :class="{ active: state === 'READY' }">READY</button>
        <button @click="setState('AWAY')" :class="{ active: state === 'AWAY' }">AWAY</button>
        <button class="logout" @click="logout">Logout</button>
      </div>
    </header>

    <main class="main">


      <section class="left">
        <h3>Assigned Chats</h3>

        <div class="tabs">
  <button class="tab" :class="{ on: tab==='OPEN' }" @click="setTab('OPEN')">OPEN</button>
  <button class="tab" :class="{ on: tab==='CLOSED' }" @click="setTab('CLOSED')">CLOSED</button>
</div>

<div class="list">
  <button
    v-for="c in assigned"
    :key="c.conversationId"
    class="conv"
    :class="{ selected: activeConv === c.conversationId }"
    @click="openConversation(c.conversationId)"
  >
    <div class="row1">
      <b>#{{ c.conversationId }}</b>
      <small class="pill" :class="c.status">{{ c.status }}</small>
    </div>
    <small>{{ c.visitor?.name || "Visitor" }} • {{ c.visitor?.phone || "" }}</small>
    <div class="preview" v-if="c.lastMessage">{{ c.lastMessage }}</div>
  </button>
</div>
      </section>

      <section class="right">
        <h3>Chat Window</h3>

        <div v-if="!activeConv" class="empty">Select a conversation…</div>

        <div v-else class="chat">
          <div class="chat-head">
            <div class="head-left">
              <b>Conversation #{{ activeConv }}</b>
              <small v-if="flowInfo" class="flowTag">
                Flow: {{ flowInfo.flow.name }} ({{ flowInfo.flow.status }})
              </small>
            </div>

            <button
              v-if="activeStatus === 'OPEN'"
              class="closebtn"
              @click="closeChat"
            >
              Close Chat
            </button>
            <small v-else class="closedText">Closed</small>
          </div>

          <!-- Flow summary box -->
          <div v-if="hasFlowSummary" class="flowBox">
            <div class="flowTitle">Captured Info</div>

            <div class="flowGrid">
              <div v-for="(val, key) in flowInfo.summary" :key="key" class="flowItem">
                <div class="k">{{ key }}</div>
                <div class="v">{{ val }}</div>
              </div>
            </div>
          </div>

          <div class="msgs" ref="msgBox">
            <div
                v-for="m in messages"
                :key="m._k"
                class="msg"
                :class="[m.side, m.senderType === 'BOT' ? 'is-bot' : '']" >
              <div class="bubble">
                  <div class="meta-bot" v-if="m.senderType === 'BOT'">🤖 Bot</div>
                  <div class="meta" v-else-if="m.senderType === 'AGENT'">🧑 Agent</div>

                  <div class="txt">{{ m.text }}</div>
                  <small class="time">{{ m.time }}</small>
                </div>
            </div>
          </div>

          <div class="composer">
            <input
              v-model="text"
              :disabled="activeStatus === 'CLOSED'"
              @keydown.enter="send"
              placeholder="Type message..."
            />
            <button :disabled="activeStatus === 'CLOSED'" @click="send">Send</button>
          </div>
        </div>
      </section>
    </main>

    <!-- POPUP -->
    <div v-if="popup" class="popup">
      <b>New Chat Assigned!</b>
      <div>Conversation: #{{ popup.conversationId }}</div>
      <div>Name: {{ popup.visitor?.name || "-" }}</div>
      <div>Phone: {{ popup.visitor?.phone || "-" }}</div>

      <div class="row">
        <button @click="openConversation(popup.conversationId)">Open</button>
        <button class="ghost" @click="popup = null">Close</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, onBeforeUnmount, computed } from "vue";
import { useRouter } from "vue-router";
import { auth } from "@/services/auth";
import { useAgentSocket } from "@/services/socket";

const API_BASE = "https://sudarshan.fonetricks.com";

const router = useRouter();
const user = ref(auth.user);

const { connect, disconnect } = useAgentSocket();

const state = ref("AWAY");
const tab = ref("OPEN"); // OPEN | CLOSED
const activeStatus = ref("OPEN"); // status of currently open conversation
const flowInfo = ref(null); 

const popup = ref(null);
const assigned = ref([]);
const activeConv = ref(null);

const messages = ref([]);
const text = ref("");
const msgBox = ref(null);

// prevent duplicates when we do optimistic UI
const pendingClientMsgIds = new Set();

function scrollBottom() {
  nextTick(() => {
    if (msgBox.value) msgBox.value.scrollTop = msgBox.value.scrollHeight;
  });
}

const hasFlowSummary = computed(() => {
  const s = flowInfo.value?.summary;
  return s && typeof s === "object" && Object.keys(s).length > 0;
});

function logout() {
  disconnect();
  auth.clear();
  router.push("/");
}


async function loadFlowSummary(conversationId) {
  flowInfo.value = null;
  try {
    const r = await fetch(`http://localhost:3000/api/conversation/${conversationId}/flow_summary`, {
      headers: { Authorization: "Bearer " + auth.token },
    });
    const data = await r.json();
    if (data.ok && data.hasFlow) {
      flowInfo.value = { flow: data.flow, summary: data.summary || {} };
    }
  } catch (e) {
    console.log("flow summary error", e);
  }
}

async function setTab(v) {
  tab.value = v;
  activeConv.value = null;
  messages.value = [];
  await loadInbox();
}



async function loadInbox() {
  try {
    const r = await fetch(`${API_BASE}/api/agent/inbox?status=${tab.value}`, {
      headers: { Authorization: "Bearer " + auth.token },
    });

    if (r.status === 401) { logout(); return; }

    const data = await r.json();
    if (data.ok) {
      assigned.value = (data.conversations || []).map((c) => ({
        conversationId: c.conversation_id,
        status: c.status,
        lastMessage: c.last_message || "",
        visitor: { name: c.name, phone: c.phone, email: c.email },
        updatedAt: c.updated_at,
      }));
    }
  } catch (e) {
    console.log("loadInbox error:", e);
  }
}

async function loadMessages(conversationId) {
  try {
    const r = await fetch(`${API_BASE}/api/conversation/${conversationId}/messages`, {
      headers: { Authorization: "Bearer " + auth.token },
    });

    if (r.status === 401) {
      logout();
      return;
    }

    const data = await r.json();
    if (data.ok) {
       messages.value = (data.messages || []).map((m) => {
        let side = "them";

        if (m.sender_type === "AGENT" || m.sender_type === "BOT") {
          side = "me"; // right side
        }

        return {
          _k: "db_" + m.id,
          text: m.body,
          side,
          senderType: m.sender_type, // ✅ keep original type
          time: new Date(m.created_at).toLocaleTimeString(),
          at: new Date(m.created_at).getTime(),
        };
      });
      scrollBottom();
    }
  } catch (e) {
    console.log("loadMessages error:", e);
  }
}

async function setState(st) {
  const s = connect();
  s.emit("agent:set_state", { state: st });

  // refresh inbox after READY
  if (st === "READY") await loadInbox();
}

async function openConversation(conversationId) {
  const s = connect();
  activeConv.value = conversationId;

  const item = assigned.value.find(x => x.conversationId === conversationId);
  activeStatus.value = item?.status || "OPEN";

  s.emit("agent:join_conversation", { conversationId });
  await loadMessages(conversationId);
  await loadFlowSummary(conversationId); // ✅ add this


  popup.value = null;
}

async function closeChat() {
  if (!activeConv.value) return;

  const r = await fetch(`${API_BASE}/api/conversation/${activeConv.value}/close`, {
    method: "POST",
    headers: { Authorization: "Bearer " + auth.token },
  });
  const data = await r.json();
  if (!data.ok) return;

  activeStatus.value = "CLOSED";
  await loadInbox(); // refresh current tab
}

function genClientMsgId() {
  return "c_" + Date.now() + "_" + Math.random().toString(36).slice(2);
}

function send() {
  const t = text.value.trim();
  if (!t || !activeConv.value) return;

  const s = connect();

  const clientMsgId = genClientMsgId();
  pendingClientMsgIds.add(clientMsgId);

  // optimistic UI
  messages.value.push({
    _k: "local_" + clientMsgId,
    text: t,
    side: "me",
    time: new Date().toLocaleTimeString(),
    at: Date.now(),
    clientMsgId,
  });
  scrollBottom();
  text.value = "";

  // send to server (include clientMsgId)
  s.emit("agent:send", {
    conversationId: activeConv.value,
    text: t,
    clientMsgId,
  });
}

let socket; // keep for cleanup

onMounted(() => {
  if (!auth.token) return router.push("/");

  socket = connect();

  // ✅ keep agent state in sync
  socket.on("agent:state", (d) => {
    if (d?.state) state.value = d.state;
  });

  socket.on("chat:closed", (e) => {
  if (activeConv.value === e.conversationId) {
    activeStatus.value = "CLOSED";
  }
  loadInbox();
});



  // load inbox on page open
  loadInbox();

  // new assignment popup + refresh inbox
  socket.on("notify", (n) => {
  if (n?.type === "new_chat_assigned") {
    popup.value = n;
    loadInbox();

    // if already viewing this conv, refresh
    if (activeConv.value && activeConv.value === n.conversationId) {
      loadFlowSummary(activeConv.value);
    }
  }
});



socket.on("chat:assigned", (e) => {
  if (activeConv.value === e.conversationId) {
        loadFlowSummary(activeConv.value);
      }
    });

      // incoming messages
    socket.on("chat:message", (msg) => {
        if (!activeConv.value || msg.conversationId !== activeConv.value) return;

        let side = "them";
        const senderType = msg.from?.type || "VISITOR";

        if (senderType === "AGENT" || senderType === "BOT") {
          side = "me"; // right side
        }

        // Avoid duplicate optimistic AGENT messages
        if (
          senderType === "AGENT" &&
          msg.clientMsgId &&
          pendingClientMsgIds.has(msg.clientMsgId)
        ) {
          pendingClientMsgIds.delete(msg.clientMsgId);
          return;
        }

        messages.value.push({
          _k: "rt_" + msg.at + "_" + Math.random().toString(36).slice(2),
          text: msg.text,
          side,
          senderType, // ✅ keep type
          time: new Date(msg.at).toLocaleTimeString(),
          at: msg.at,
        });

        scrollBottom();
      });


});

onBeforeUnmount(() => {
  if (!socket) return;

  socket.off("agent:state");
  socket.off("notify");
  socket.off("chat:message");
  socket.off("chat:closed");

});
</script>

<style scoped>
.wrap { font-family: Arial; }
.top {
  display:flex; justify-content:space-between; align-items:center;
  padding: 12px 16px; border-bottom: 1px solid #eee;
}
.controls { display:flex; gap:8px; align-items:center; }
.state { margin-right: 10px; }
button {
  padding: 8px 10px; border: none; border-radius: 8px; cursor: pointer;
  background: #0b66d1; color: #fff;
}
button.active { background: #0a53a6; }
button.logout { background: #444; }
button.ghost { background: #eee; color:#000; }

.main { display:grid; grid-template-columns: 320px 1fr; height: calc(100vh - 58px); }
.left { border-right: 1px solid #eee; padding: 12px; overflow:auto; }
.right { padding: 12px; display:flex; flex-direction:column; }
.muted { color:#666; font-size: 13px; }

.list { display:flex; flex-direction:column; gap:8px; margin-top: 10px; }
.conv { text-align:left; background:#f7f7f7; color:#000; border: 1px solid #eee; }
.conv:hover { background:#f0f0f0; }
.conv.selected { border-color: #0b66d1; }

.empty { color:#777; padding: 20px; border: 1px dashed #ccc; border-radius: 12px; }

.chat { display:flex; flex-direction:column; flex:1; border: 1px solid #eee; border-radius: 12px; overflow:hidden; }
.chat-head { padding: 10px; border-bottom: 1px solid #eee; background:#fafafa; }
.msgs { flex:1; padding: 10px; overflow:auto; background:#fff; }
.msg { margin-bottom: 8px; display:flex; }
.msg.me { justify-content:flex-end; }
.msg.them { justify-content:flex-start; }
.bubble {
  max-width: 75%;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid #eee;
  background: #f6f6f6;
}
.msg.me .bubble { background:#0b66d1; color:#fff; border-color:#0b66d1; }
.time { display:block; opacity:0.8; font-size: 11px; margin-top: 4px; }

.composer { display:flex; gap:8px; padding: 10px; border-top:1px solid #eee; }
.composer input { flex:1; padding: 10px; border-radius: 8px; border:1px solid #ccc; }

.popup {
  position: fixed; right: 16px; top: 16px;
  width: 280px;
  background:#fff; border:1px solid #ddd; border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  padding: 12px;
}
.row { display:flex; gap:8px; margin-top: 10px; }


.tabs { display:flex; gap:8px; margin-bottom: 10px; }
.tab {
  background:#eee; color:#000;
  padding: 8px 10px; border-radius: 10px;
}
.tab.on { background:#0b66d1; color:#fff; }

.row1 { display:flex; justify-content:space-between; align-items:center; gap:8px; }
.pill {
  padding: 2px 8px; border-radius: 999px;
  border: 1px solid #ddd; font-size: 11px;
}
.pill.OPEN { background:#e9f6ee; border-color:#bfe6cc; }
.pill.CLOSED { background:#f5f5f5; border-color:#ddd; }

.preview {
  margin-top: 6px;
  font-size: 12px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.closebtn {
  margin-left: auto;
  background: #e74c3c;
  color: #fff;
  padding: 6px 10px;
  border-radius: 10px;
  float: right;
}
.closedText { margin-left:auto; color:#777; }


.head-left { display:flex; flex-direction:column; gap:4px; }

.flowTag {
  font-size: 12px;
  color: #555;
}

.flowBox {
  border-bottom: 1px solid #eee;
  padding: 10px;
  background: #fafafa;
}

.flowTitle {
  font-weight: 700;
  font-size: 12px;
  margin-bottom: 8px;
  color: #333;
}

.flowGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.flowItem {
  border: 1px solid #eee;
  background: #fff;
  border-radius: 10px;
  padding: 8px;
}

.flowItem .k {
  font-size: 11px;
  color: #666;
  text-transform: capitalize;
}

.flowItem .v {
  font-size: 13px;
  color: #111;
  margin-top: 3px;
  word-break: break-word;
}


.msg.bot { justify-content: flex-start; }

.msg.bot .bubble {
  background: #0a66d1b3;
  border-color: #0a66d1b3;
  color: #3b0764;
}

.tag {
  display: block;
  font-size: 10px;
  opacity: 0.7;
  margin-bottom: 4px;
}

.meta {
  font-size: 10px;
  font-weight: bold;
  color: #fff;
  opacity: 0.8;
  margin-bottom: 4px;
}

.meta-bot {
  font-size: 10px;
  font-weight: bold;
  color: red;
  opacity: 0.8;
  margin-bottom: 4px;
}

.msg.me .meta {
  text-align: right;
}

.msg.me .meta-bot {
  text-align: right;
}


/* BOT messages get a special tint */
.msg.is-bot .bubble {
  background: #7c3aed;       /* violet */
  border-color: #7c3aed;
  color: #7c3aed;
}

/* Optional: slightly different time color for bot */
.msg.is-bot .time {
  color: rgba(255, 255, 255, 0.8);
}

/* Optional: tweak meta label color */
.msg.is-bot .meta {
  color: #e9d5ff;
}

.msg.is-bot .bubble {
  background: #8b7ec3;   /* light violet */
  border-color: #c4b5fd;
  color: #1f2937;
}


</style>